// src/hooks/useFieldGroups.ts
// Hook to fetch field group metadata (event-type relevance, categories) from backend

import { useEffect, useState, useCallback } from "react";
import { fetchData } from "api/apiHelpers";
import logger from "utils/logger";

export interface FieldGroupsResponse {
  domain_key: string;
  table_type: string;
  model_name: string;
  field_event_categories?: Record<string, string[]>;
  field_categories?: Record<string, string[]>;
  /**
   * Groups event-type *values* (not fields) into clinical categories. Used
   * by AddEventButton / inline event_type editor to render subheaders.
   * Keyed by display category, values are the event_type slugs.
   */
  event_type_categories?: Record<string, string[]>;
}

// Cache for field group payloads
const fieldGroupsCache = new Map<string, FieldGroupsResponse>();

export function useFieldGroups(domainKey: string, tableType: string) {
  const [fieldGroups, setFieldGroups] = useState<FieldGroupsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cacheKey = `${domainKey}:${tableType}`;

  useEffect(() => {
    if (!domainKey || !tableType) return;

    const fetchFieldGroups = async () => {
      // Check cache first
      if (fieldGroupsCache.has(cacheKey)) {
        setFieldGroups(fieldGroupsCache.get(cacheKey)!);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await fetchData<FieldGroupsResponse>(
          `/field-groups/${domainKey}/${tableType}/`
        );
        fieldGroupsCache.set(cacheKey, data);
        setFieldGroups(data);
      } catch (err: any) {
        logger.error(`[useFieldGroups] Error fetching field groups:`, err);
        setError(err.message || "Failed to fetch field groups");
      } finally {
        setLoading(false);
      }
    };

    fetchFieldGroups();
  }, [domainKey, tableType, cacheKey]);

  const getFieldsForEventType = useCallback(
    (eventType: string): string[] => {
      const rel = fieldGroups?.field_event_categories;
      if (!rel) return [];
      const universal = rel["_universal"] ?? [];
      const specific = rel[eventType] ?? [];
      const seen = new Set<string>();
      const out: string[] = [];
      for (const f of universal) {
        if (!seen.has(f)) {
          seen.add(f);
          out.push(f);
        }
      }
      for (const f of specific) {
        if (!seen.has(f)) {
          seen.add(f);
          out.push(f);
        }
      }
      return out;
    },
    [fieldGroups]
  );

  const getFieldCategories = useCallback((): Record<string, string[]> => {
    return fieldGroups?.field_categories ?? {};
  }, [fieldGroups]);

  /** Reverse lookup: event_type slug → category display name. */
  const getCategoryForEventType = useCallback(
    (eventType: string): string | undefined => {
      const cats = fieldGroups?.event_type_categories;
      if (!cats) return undefined;
      for (const [category, slugs] of Object.entries(cats)) {
        if (slugs.includes(eventType)) return category;
      }
      return undefined;
    },
    [fieldGroups]
  );

  const getEventTypeCategories = useCallback((): Record<string, string[]> => {
    return fieldGroups?.event_type_categories ?? {};
  }, [fieldGroups]);

  return {
    fieldGroups,
    loading,
    error,
    getFieldsForEventType,
    getFieldCategories,
    getEventTypeCategories,
    getCategoryForEventType,
  };
}

// Clear field groups cache (useful after model changes)
export function invalidateFieldGroupsCache(domainKey?: string, tableType?: string) {
  if (domainKey && tableType) {
    fieldGroupsCache.delete(`${domainKey}:${tableType}`);
  } else {
    fieldGroupsCache.clear();
  }
}

export default useFieldGroups;
